import { Translation } from '@ps/web-shared-components';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Iframe from '@swf/components/Iframe/Iframe';
import { useTouchpoint } from '@swf/components/Touchpoint';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import { CesFeedbackFormFlowType } from '../../types/Cashier';
import { checkCesFormExpirationStatus, getNextDaysTimestamp } from '../../utils/cashier';

import { TXTMOB_Feedback } from './CesFeedbackForm.messages.json';
import { crm, embedder, line } from './CesFeedbackForm.scss';

interface Props {
  skipFeedbackForm: boolean;
  flow: CesFeedbackFormFlowType;
}

const EXPIRE_DATE_KEY_MAP = {
  [CesFeedbackFormFlowType.ConvertCurrency]: 'convertCurrencyExpireDate',
  [CesFeedbackFormFlowType.Deposit]: 'depositExpireDate',
  [CesFeedbackFormFlowType.P2pTransfer]: 'p2pTransferExpireDate',
  [CesFeedbackFormFlowType.TransactionHistory]: 'transactionHistoryExpireDate',
  [CesFeedbackFormFlowType.Withdraw]: 'withdrawExpireDate'
} as const;

const CesFeedbackForm: React.FC<Props> = ({ skipFeedbackForm, flow }) => {
  const { localSettings } = useDispatch<Dispatch>();
  const cesFeedbackForm = useSelector(select.localSettings.getCashier)?.cesFeedbackForm;

  const expireDateKey = EXPIRE_DATE_KEY_MAP[flow];
  const cesFeedbackFormExpireDate = cesFeedbackForm?.[expireDateKey] || 0;
  const showCesForm = checkCesFormExpirationStatus(cesFeedbackFormExpireDate);

  const skipTouchpoint = skipFeedbackForm || !showCesForm;
  const { isReady, getEmbeddedUrl } = useTouchpoint(skipTouchpoint, true);

  useEffect(() => {
    if (isReady) {
      localSettings.set({
        cashier: {
          cesFeedbackForm: { [expireDateKey]: getNextDaysTimestamp(new Date(), 30) }
        }
      });
    }
  }, [isReady, expireDateKey, localSettings]);

  const embeddedDistributionUrl = isReady && getEmbeddedUrl();

  return embeddedDistributionUrl ? (
    <div className={crm} data-testid="ces-feedback-form">
      <span className={line}>
        <h2>
          <Translation tagName="span" {...TXTMOB_Feedback} />
        </h2>
        <div className={embedder}>
          <Iframe src={embeddedDistributionUrl} scrolling="no" />
        </div>
      </span>
    </div>
  ) : null;
};

export default CesFeedbackForm;
